import React from 'react';

/**
 * Sezione “Messaggi Privati” — lista conversazioni (i tuoi match) e accesso
 * alla chat DM già esistente. Evidenzia la conversazione con messaggi non letti.
 *
 * Props:
 *  - matches   : array di { match: { userId, photo, firstName, lastName, arrivalNumber }, eventName, eventDate, city, isSuper }
 *  - latest    : ultimo messaggio non letto da GET /messages/unread (o null)
 *  - unreadCount : totale messaggi non letti (per l'empty state)
 *  - onChat(match) : apre la Chat DM con il match
 */
const Messages = ({ matches, latest, unreadCount, onChat }) => {
    const convos = Array.isArray(matches) ? matches : [];

    return (
        <div className="mx-auto w-100 fade-in" style={{ maxWidth: '800px' }}>
            <h2 className="app-section-title"><i className="fas fa-comment-dots"></i> Messaggi Privati</h2>

            {convos.length === 0 ? (
                <div className="card glass-premium border-0 p-5 text-center shadow-lg fade-in-up" style={{ borderRadius: '45px' }}>
                    <div className="mb-4 opacity-20">
                        <i className="fas fa-comment-slash fa-4x text-pink-600"></i>
                    </div>
                    <h4 className="fw-700 text-dark mb-2">Ancora nessuna conversazione</h4>
                    <p className="text-muted fw-600 mb-0">I tuoi match compariranno qui e potrai scrivergli in privato.</p>
                </div>
            ) : (
                <div className="d-flex flex-column gap-3">
                    {convos.map((m, idx) => {
                        const hasUnread = latest && Number(latest.senderId) === Number(m.match?.userId);
                        return (
                            <div key={idx} className="card glass-premium rounded-5 p-4 shadow-sm fade-in-up"
                                style={{
                                    background: hasUnread ? 'rgba(253, 242, 248, 0.95)' : 'rgba(255,255,255,0.9)',
                                    border: hasUnread ? '2px solid #db2777' : '1px solid rgba(219,39,119,0.1)',
                                    animationDelay: `${Math.min(idx * 0.06, 0.5)}s`
                                }}>
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                    <div className="rounded-circle bg-white shadow-sm overflow-hidden d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: '58px', height: '58px', border: hasUnread ? '2px solid #db2777' : '2px solid #f8f9fa' }}>
                                        {m.match?.photo ? (
                                            <img src={m.match.photo} alt={m.match.firstName || 'Match'} className="w-100 h-100 object-fit-cover" />
                                        ) : (
                                            <i className="fas fa-user text-pink-600 fs-4"></i>
                                        )}
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <h4 className="fw-900 text-dark mb-1 text-truncate" style={{ fontSize: '1.05rem' }}>
                                            {m.match?.firstName || 'Utente'} {m.match?.lastName || ''}
                                            {m.match?.arrivalNumber && (
                                                <span className="ms-2 badge bg-light text-pink-600 rounded-pill border border-pink-100" style={{ fontSize: '0.62rem' }}>#{m.match.arrivalNumber}</span>
                                            )}
                                        </h4>
                                        <div className="text-muted fw-600 small text-truncate">
                                            <i className="fas fa-calendar-check me-2 text-pink-600"></i>
                                            {m.eventName} — {new Date(m.eventDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </div>
                                        {hasUnread && (
                                            <span className="badge rounded-pill px-3 py-1 small fw-800 mt-1" style={{ background: 'linear-gradient(135deg, #db2777, #f43f5e)', color: '#fff' }}>
                                                <i className="fas fa-circle me-1" style={{ fontSize: '0.5rem' }}></i> NUOVO MESSAGGIO
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex-shrink-0">
                                        <button
                                            onClick={() => onChat(m.match)}
                                            disabled={!m.match?.userId}
                                            className="btn btn-danger rounded-pill px-4 py-2 shadow-sm border-0 fw-900"
                                            style={{ background: 'linear-gradient(45deg, #db2777, #f43f5e)', fontSize: '0.75rem' }}
                                        >
                                            <i className="fas fa-comment me-2"></i>Inviagli un messaggio
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {unreadCount > 0 && (
                <div className="text-muted small fw-600 mt-4 text-center">
                    <i className="fas fa-circle me-2" style={{ color: '#db2777', fontSize: '0.5rem' }}></i>
                    Hai {unreadCount} messagg{unreadCount === 1 ? 'io non letto' : 'i non letti'}
                </div>
            )}
        </div>
    );
};

export default Messages;